import { MegaMenu } from "primereact/megamenu";
import { InputText } from "primereact/inputtext";
import { MenuItem } from "primereact/menuitem";
import { Button } from "primereact/button";
import { useState, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/icon-01.png";

// Define searchable content
const searchableContent = [
  { keyword: "home", path: "/" },
  { keyword: "about", path: "/about" },
  { keyword: "story", path: "/about", section: "our-story" },
  { keyword: "journey", path: "/about", section: "our-journey" },
  { keyword: "process", path: "/about", section: "our-process" },
  { keyword: "services", path: "/services" },
  { keyword: "core services", path: "/services", section: "core-services" },
  { keyword: "why choose", path: "/services", section: "why-choose-us" },
  { keyword: "contact", path: "/contact" },
  { keyword: "form", path: "/contact", section: "contact-form" },
  { keyword: "email", path: "/contact", section: "contact-info" },
  { keyword: "call", path: "/contact", section: "contact-info" },
];

export default function Navbar() {
  const [theme, setTheme] = useState<"light" | "dark">(() =>
    document.documentElement.getAttribute("data-theme") === "dark"
      ? "dark"
      : "light"
  );

  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    document.documentElement.setAttribute("data-theme", next);
    localStorage.setItem("theme", next);
  };

  useLayoutEffect(() => {
    const saved =
      (localStorage.getItem("theme") as "light" | "dark" | null) || null;
    if (saved) {
      setTheme(saved);
      document.documentElement.setAttribute("data-theme", saved);
    }
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    if (!searchQuery.trim()) return;

    const query = searchQuery.toLowerCase();
    const result = searchableContent.find(
      (item) => item.keyword.includes(query) || query.includes(item.keyword)
    );

    if (result) {
      navigate(result.path);
      if (result.section) {
        setTimeout(() => {
          document
            .getElementById(result.section!)
            ?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }

    setSearchQuery("");
  };

  const items: MenuItem[] = [
    {
      label: "Home",
      icon: "pi pi-fw pi-home",
      command: () => navigate("/"),
    },
    {
      label: "About",
      icon: "pi pi-fw pi-sparkles",
      items: [
        [
          {
            label: "About Us",
            command: () => navigate("/about"),
            items: [
              { label: "Our Story", command: () => navigate("/about") },
              {
                label: "Our Journey",
                command: () => {
                  navigate("/about");
                  setTimeout(() => {
                    document
                      .getElementById("our-journey")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                },
              },
              {
                label: "Our Process",
                command: () => {
                  navigate("/about");
                  setTimeout(() => {
                    document
                      .getElementById("our-process")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                },
              },
            ],
          },
        ],
      ],
    },
    {
      label: "Services",
      icon: "pi pi-fw pi-th-large",
      items: [
        [
          {
            label: "Our Services",
            command: () => navigate("/services"),
            items: [
              { label: "Overview", command: () => navigate("/services") },
              {
                label: "Core Services",
                command: () => {
                  navigate("/services");
                  setTimeout(() => {
                    document
                      .getElementById("core-services")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                },
              },
              {
                label: "Why Choose Us",
                command: () => {
                  navigate("/services");
                  setTimeout(() => {
                    document
                      .getElementById("why-choose-us")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                },
              },
            ],
          },
        ],
      ],
    },
    {
      label: "Contact",
      icon: "pi pi-envelope",
      items: [
        [
          {
            label: "Contact Us",
            command: () => navigate("/contact"),
            items: [
              { label: "Overview", command: () => navigate("/contact") },

              {
                label: "Contact Form",
                command: () => {
                  navigate("/contact");
                  setTimeout(() => {
                    document
                      .getElementById("contact-form")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                },
              },
              {
                label: "Email or Call",
                command: () => {
                  navigate("/contact");
                  setTimeout(() => {
                    document
                      .getElementById("contact-info")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                },
              },
            ],
          },
        ],
      ],
    },
  ];

  const start = (
    <div className="flex items-center" style={{ minWidth: "200px" }}>
      <img alt="logo" src={logo} className="h-10 w-auto" />
    </div>
  );

  const end = (
    <div
      className="flex items-center gap-3 justify-end"
      style={{ minWidth: "280px" }}
    >
      <form onSubmit={handleSearch} className="relative">
        <InputText
          placeholder="Search"
          type="text"
          className="w-56"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <i
          className="pi pi-search absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer"
          onClick={handleSearch}
        />
      </form>
      <Button
        icon={theme === "light" ? "pi pi-moon" : "pi pi-sun"}
        rounded
        text
        onClick={toggleTheme}
        aria-label="Toggle theme"
        tooltip={
          theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"
        }
      />
    </div>
  );

  return (
    <div className="w-full shadow-sm">
      <div className="container mx-auto px-6 py-2">
        <MegaMenu
          model={items}
          orientation="horizontal"
          start={start}
          end={end}
          breakpoint="960px"
        />
      </div>
    </div>
  );
}
